import { FC, useEffect, useRef, useState } from 'react'
import styles from './Pod4NewBase.module.scss'
import { Pod4NewBaseProps } from './types'
import cx from 'classnames'
import { Img } from 'components/basic/Img'
import { VideoGif } from 'components/basic/VideoGif'
import { useSlider } from 'components/_hooks/useSlider'
import { Button } from 'components/Phantom/Button'
import { observer } from 'mobx-react'
import { useRootStore } from 'components/_hooks/useRootStore'

interface NewBaseItem {
	imgUrl: string
	mobImgUrl: string
	poster?: string
	type?: string
	description: string
}

const SLIDE_DURATION = 5 * 1000

const getCurrentSlideIndex = (time: number, numItems: number) => {
	return Math.floor(time / SLIDE_DURATION) % numItems
}

export const TabbedMedia = ({ items }: { items: NewBaseItem[] }) => {
	const [selected, setSelected] = useState(0)
	const currentAnimTime = useRef(0)

	useEffect(() => {
		let lastTime = 0
		let animationId = 0
		let lastSlideIndex = 0

		const animate = (time: number) => {
			const delta = time - lastTime
			lastTime = time
			currentAnimTime.current += delta

			// Reset the animation time if we've reached the end
			if (currentAnimTime.current >= items.length * SLIDE_DURATION) {
				currentAnimTime.current = 0
			}

			const currentSlide = getCurrentSlideIndex(currentAnimTime.current, items.length)

			// If we detect a slide transition, update the selected state
			if (currentSlide !== lastSlideIndex) {
				setSelected(currentSlide)
				lastSlideIndex = currentSlide
			}

			for (let i = 0; i < items.length; i++) {
				const progressElement = document.getElementById(`new-base-progress-${i}`)
				if (!progressElement) {
					continue
				}

				if (i !== currentSlide) {
					progressElement.style.opacity = '0'
					continue
				}

				const slideStartTime = i * SLIDE_DURATION
				const slideProgress = (currentAnimTime.current - slideStartTime) / SLIDE_DURATION

				progressElement.style.opacity = '1'
				progressElement.style.width = `${Math.min(slideProgress, 1) * 100}%`
			}

			animationId = requestAnimationFrame(animate)
		}

		animationId = requestAnimationFrame(animate)

		return () => {
			cancelAnimationFrame(animationId)
		}
	}, [])

	useEffect(() => {
		currentAnimTime.current = selected * SLIDE_DURATION
	}, [selected])

	return (
		<div className={styles.tabContainer}>
			<div className={styles.media_container}>
				{items.map((item, index) => (
					<div
						style={{ opacity: selected === index ? 1 : 0, visibility: selected === index ? 'visible' : 'hidden' }}
						key={item.imgUrl}
					>
						{item.type === 'video' ? (
							<VideoGif
								src={item.imgUrl}
								poster={item.poster}
								className={styles.video}
							/>
						) : (
							<Img
								alt=""
								src={item.imgUrl}
								objectFit={'cover'}
							/>
						)}
					</div>
				))}
			</div>
			<div className={styles.tabs}>
				{items.map((item, index) => (
					<div
						onClick={() => setSelected(index)}
						key={item.description}
						className={cx(styles.tab, selected === index ? styles.tabSelected : '')}
					>
						<div
							id={`new-base-progress-${index}`}
							className={styles.tab_progress}
						/>
						{item.description}
					</div>
				))}
			</div>
		</div>
	)
}

const Slide = ({ mobImgUrl, type, description, poster }: NewBaseItem) => (
	<div className={styles.slide}>
		{type === 'video' ? (
			<VideoGif
				src={mobImgUrl}
				className={styles.sliderVideo}
				poster={poster}
			/>
		) : (
			<Img
				alt=""
				className={styles.sliderImg}
				src={mobImgUrl}
				objectFit={'cover'}
				dprHeight={800}
			/>
		)}
	</div>
)

const MobileVideo = ({ items }: { items: NewBaseItem[] }) => {
	const [sliderRef, currentSlide, goToSlide] = useSlider({ refreshToken: items?.length, throwResistance: 5000 })

	return (
		<>
			<div className={styles.slider_outer}>
				<ul
					className={styles.slider}
					ref={sliderRef}
				>
					{items.map((item, i) => (
						<li key={i}>
							<Slide {...item} />
						</li>
					))}
				</ul>
			</div>

			<ul className={styles.descriptions}>
				{items.map((item, i) => (
					<li
						key={`pod4-base-desc-${i}`}
						style={{ opacity: currentSlide === i ? 1 : 0, visibility: currentSlide === i ? 'visible' : 'hidden' }}
					>
						<p>{item.description}</p>
					</li>
				))}
			</ul>

			<ul className={styles.dots}>
				{items.map((item, i) => (
					<li
						key={i}
						style={{ opacity: currentSlide === i ? 1 : 0.2 }}
						onClick={() => goToSlide(i)}
					>
						<Button.Empty id={`new-base-dot-${i}`} />
					</li>
				))}
			</ul>
		</>
	)
}

export const NewBaseConnected = observer(() => {
	const { metricRegion } = useRootStore().settingsStore
	return <Pod4NewBase metric={metricRegion} />
})

export const Pod4NewBase: FC<Pod4NewBaseProps> = (props) => {
	const { metric } = props

	const depth = metric ? '7 cm' : '3”'
	const weight = metric ? '340 kg' : '750 lbs'

	const items = [
		{
			imgUrl: 'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/new_base_with_hub_ecosystem.png',
			mobImgUrl: 'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/new_base_mob_3_v2.jpg',
			description: 'Fits into your existing bed frame',
		},
		{
			imgUrl: metric ? 'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/desktop_new_base_depth_metric.jpg' : 'https://eightsleep.imgix.net/assets/new_base_2_1529.png',
			mobImgUrl: metric
				? 'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/mobile_new_base_depth_metric.jpg'
				: 'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/new_base_mob_1_v2.jpg',
			type: 'cover',
			description: `Disappears in your bed frame, being only ${depth} tall`,
		},
		{
			imgUrl: 'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/sandwhich_slider_new.mp4',
			mobImgUrl: 'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/sandwhich_slider_new.mp4',
			poster: 'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/sandwhich_first_frame.jpg',
			type: 'video',
			description: `Holds up to ${weight} and works with any mattress`,
		},
		{
			imgUrl: 'https://eightsleep.imgix.net/assets/new_base_4_1356.png',
			mobImgUrl: 'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/new_base_mob_4_v2.jpg',
			description: 'Moves both sides at the same time, quietly and smoothly',
		},
	]

	return (
		<div className={styles.container}>
			<div className={styles.content}>
				<div className={styles.pitch}>
					<div className={styles.pill}>New</div>
					<div className={styles.title}>
						<div>A new Base added to the Pod ecosystem</div>
					</div>
					<div className={styles.description}>
						The new Base syncs with the Pod’s technology for additional groundbreaking features like snoring mitigation, and adjustable body positions before and during sleep.
					</div>
				</div>
				<div className={styles.sliderContainer}>
					<MobileVideo items={items} />
				</div>
				<TabbedMedia items={items} />
			</div>
		</div>
	)
}
